/* eslint-disable max-lines */
import { Component, Input, OnDestroy, OnInit, Output, EventEmitter, ViewChild } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { AccountLabels, AdminCompanyDetails, ApproveBusinessAdminPayload, BusinessAccountRequest, BusinessAccountRequestState, BusinessAccountsGridData, EditAdminCompanyDetailsAemLabels, ValidateAdminForm } from './definitions';
import { BusinessAccountRequestService } from './business-account-request.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AprRejActionData, AprRejBtnType, ButtonState, ErrorMessageHeading, ErrorMessageSubHeading, GridValue, KngDialogTooltipComponent, KngLoaderState, KngUploadFileComponent, ModalComponent, ModalEvent, ModalState } from 'kinetic-angular';
import { Overlay } from '@angular/cdk/overlay';
import { CssClass, Icons, ModalButtonId, countries, provinces, states } from '../../services/shared/shared-enums';
import { SharedService } from '../../services/shared/shared.service';
import { SharedUtilService } from '../../services/shared/shared-util.service';
import { MatSelectChange } from '@angular/material/select';
import { ControlName } from '../../services/shared/shared-mapping';
import { AnalyticsDataLayer } from '../../services/analytics/definitions';
import { AnalyticsService } from '../../services/analytics/analytics.service';


@Component({
	selector: 'tmhna-portal-business-account-request',
	templateUrl: './template/business-account-request.component.html',
	styleUrls: ['./template/business-account-request.component.scss']
})
export class BusinessAccountRequestComponent implements BusinessAccountRequest, OnInit, OnDestroy 
{

	private subscriptions: Subscription = new Subscription();
	private statesOrProvinces$: BehaviorSubject<{ label: string; value: string }[]> = new BehaviorSubject<{ label: string; value: string }[]>(
		[]
	);
	countries = countries;
	validateEditForm: ValidateAdminForm = {
		accountName: false,
		addressLine1: false,
		country: false,
		city: false,
		state: false,
		postalCode: false
	};
	tempUserId: string = '';
	loaderState: KngLoaderState = { imagePath: Icons.loaderWhite };
	dialogRef!: MatDialogRef<KngDialogTooltipComponent>;

	@ViewChild('editAdminCompanyDetailsModal') editAdminCompanyDetailsModal!: ModalComponent;
	@ViewChild('uploadFile') uploadFile!: ModalComponent;
	@ViewChild(KngUploadFileComponent, { static: false }) fileUploadComponent: | KngUploadFileComponent | undefined;

	@Output() isDirty = new EventEmitter<boolean>();
	@Output() stateChanged: EventEmitter<BusinessAccountRequestState> = new EventEmitter<BusinessAccountRequestState>();

	@Input() set state(value: BusinessAccountRequestState) 
	{
		this.service.updateState(value);
	}
	get state(): BusinessAccountRequestState 
	{
		return this.service.state;
	}
	set statesValue(value: { label: string; value: string }[]) 
	{
		this.statesOrProvinces$.next(value);
	}
	get statesValue(): { label: string; value: string }[] 
	{
		return this.statesOrProvinces$.value;
	}
	get editAdminCompanyDetailsModalState(): ModalState 
	{
		return this.service.editAdminCompanyDetailsModalState;
	}
	@Input() set editAdminCompanyDetailsAemLabelsState(value: EditAdminCompanyDetailsAemLabels) 
	{
		this.service.updateEditAdminCompanyDetailsState(value);
	}
	get editAdminCompanyDetailsAemLabelsState(): EditAdminCompanyDetailsAemLabels 
	{
		return this.service.editAdminCompanyDetailsAemLabelsState;
	}
	get buildEditDetailsForm() 
	{
		return this.service.buildEditDetailsForm;
	}

	constructor(public service: BusinessAccountRequestService, private sharedService: SharedService,
		public dialog: MatDialog, private overlay: Overlay, private sharedUtilService: SharedUtilService,
		private analyticsService: AnalyticsService) { }

	ngOnInit(): void 
	{
		this.initSubscriptions();
		this.service.getBusinessAccountRequestPageAemLabel();
		this.service.getEditAdminCompanyDetailsAemLabels();
		this.service.buildEditDetailsForm?.get('country')?.valueChanges.subscribe({
			next: value => 
			{
				this.statesValue = [];
				if (value?.includes('US')) 
				{
					setTimeout(() => 
					{
						this.statesValue = states;
					}, 0);
					this.service.buildEditDetailsForm.controls['postalCode'].removeValidators([this.sharedService.zipCodeCanadaValidCheck]);
					this.service.buildEditDetailsForm.controls['postalCode'].addValidators([this.sharedService.zipCodeLengthCheck]);
					this.service.buildEditDetailsForm.controls['postalCode'].updateValueAndValidity();
				}
				else if (value?.includes('CA')) 
				{
					setTimeout(() => 
					{
						this.statesValue = provinces;
					}, 0);
					this.service.buildEditDetailsForm.controls['postalCode'].removeValidators([this.sharedService.zipCodeLengthCheck]);
					this.service.buildEditDetailsForm.controls['postalCode'].addValidators([this.sharedService.zipCodeCanadaValidCheck]);
					this.service.buildEditDetailsForm.controls['postalCode'].updateValueAndValidity();
				}
			}
		});
		this.service.isAdminDetailsUpdated$.subscribe(data => 
		{
			if (data) 
			{
				this.closeModal();
			}
		});
		this.trackAnalyticsOnLoad();
	}
	trackAnalyticsOnLoad()
	{
		const analyticsData: AnalyticsDataLayer = {
			event: 'pageView',
			page: {
				name: 'Business Account Request Management'
			}
		};
		this.analyticsService.trackByEvent(analyticsData);
	}
	ngOnDestroy(): void 
	{
		this.destroySubscriptions();
	}

	private initSubscriptions(): void 
	{
		const stateChangeSubs: Subscription = this.service.stateChanged.subscribe(
			(next: BusinessAccountRequestState) => this.stateChanged.emit(next)
		);
		this.subscriptions.add(stateChangeSubs);
	}
	private destroySubscriptions(): void 
	{
		this.subscriptions.unsubscribe();
	}
	showTooltip(id: string) 
	{
		this.dialogRef = this.dialog.open(KngDialogTooltipComponent, {
			width: '320px',
			data: { state: this.state.sectionMiddle.tableState.tooltip, positionRelativeToElement: document.getElementById('tooltip-' + id) },
			restoreFocus: true,
			scrollStrategy: this.overlay.scrollStrategies.noop(),
			panelClass: 'kng-dialog-tooltip-container'
		});
		this.dialogRef?.afterClosed().subscribe(
			() => 
			{
				this.dialogRef.close();
			});
	}

	approveRejectUser(data: AprRejActionData) 
	{
		if (data?.action === AprRejBtnType.singleReject) 
		{
			this.service.rejectUsers(data.selectedUserData);
		}
		if (data?.action === AprRejBtnType.singleApprove)
		{
			const approveBusinessAdminPayload: ApproveBusinessAdminPayload = {
				tempUserId: data.selectedUserDataForApprove[0]?.id
			};
			this.service.approveBusinessAdmin(approveBusinessAdminPayload);
		}
	}
	openEditAdminCompanyDetailsModal(id: string) 
	{
		this.service.updateAdminDetailsErrorMessage = '';
		this.sharedUtilService.hideModalBackdrop();
		this.editAdminCompanyDetailsModalState.hidden = false;
		this.editAdminCompanyDetailsModalState.label = this.editAdminCompanyDetailsAemLabelsState.heading;
		const businessAccountData: BusinessAccountsGridData | undefined = this.state.sectionMiddle.businessAccountData.find((data) =>
			data.tempUserID === id
		);
		if (businessAccountData) 
		{
			this.tempUserId = businessAccountData.tempUserID;
			this.service.patchEditDetailsForm(businessAccountData);
		}
	}


	editAdminCompanyDetailsModalEvent(event: ModalEvent) 
	{
		if (event.cta.id === ModalButtonId.cancel) 
		{
			this.closeModal();
		}
		else if (this.isEditAdminFormValid()) 
		{
			this.service.updateAdminCompanyDetails(this.getUpdateAdminCompanyDetailsPayload());
		}
	}

	getUpdateAdminCompanyDetailsPayload(): AdminCompanyDetails 
	{
		const payload: AdminCompanyDetails = {
			companyName: this.service.buildEditDetailsForm.controls['accountName']['value'],
			addressLine1: this.service.buildEditDetailsForm.controls['addressLine1']['value'],
			addressLine2: this.service.buildEditDetailsForm.controls['addressLine2']['value'],
			country: this.service.buildEditDetailsForm.controls['country']['value'],
			city: this.service.buildEditDetailsForm.controls['city']['value'],
			state: this.service.buildEditDetailsForm.controls['state']['value'],
			zipCode: this.service.buildEditDetailsForm.controls['postalCode']['value'],
			tempUserID: this.tempUserId
		};
		return payload;
	}

	isEditAdminFormValid(): boolean 
	{
		this.validateEditForm = {
			accountName: this.validateEditAdminCompanyDetailsForm('accountName'),
			addressLine1: this.validateEditAdminCompanyDetailsForm('addressLine1'),
			country: this.validateEditAdminCompanyDetailsForm('country'),
			city: this.validateEditAdminCompanyDetailsForm('city'),
			state: this.validateEditAdminCompanyDetailsForm('state'),
			postalCode: this.validateEditAdminCompanyDetailsForm('postalCode')
		};
		return !(this.isFormFieldCheck());
	}

	isFormFieldCheck(): boolean 
	{
		return (this.validateEditForm.accountName || this.validateEditForm.addressLine1 || this.validateEditForm.country || this.validateEditForm.city
			|| this.validateEditForm.state || this.validateEditForm.postalCode);
	}

	closeModal() 
	{
		this.editAdminCompanyDetailsModalState.hidden = false;
		this.editAdminCompanyDetailsModal?.closePopup();
		this.editAdminCompanyDetailsModalState.hidden = true;
	}

	public validateEditAdminCompanyDetailsForm(field: string): boolean 
	{
		return (
			this.buildEditDetailsForm.controls[field]?.invalid &&
			(this.buildEditDetailsForm.controls[field].dirty ||
				this.buildEditDetailsForm.controls[field].touched)
		);
	}

	countryChange(event: MatSelectChange) 
	{
		this.isDirty.emit(true);
		if (event) 
		{
			this.service.buildEditDetailsForm.controls['state'].reset();
			this.service.buildEditDetailsForm.controls['state'].setValue('');
			this.service.buildEditDetailsForm.controls['postalCode'].reset();
			this.service.buildEditDetailsForm.controls['city'].reset();
			this.service.buildEditDetailsForm.controls['state'].markAsDirty();
			this.service.buildEditDetailsForm.controls['postalCode'].markAsDirty();
			this.service.buildEditDetailsForm.controls['city'].markAsDirty();
		}
	}
	getRequiredErrorMessage(fieldName: string) 
	{
		return this.sharedService.updateMessage(this.editAdminCompanyDetailsAemLabelsState.errorText.requiredErrorMessage, '$token', fieldName);
	}

	formatZipCode(value: string) 
	{
		this.isDirty.emit(true);
		this.service.formatZipCode(value);
	}
	nonNumericSpaceRestriction(value: string, name: string) 
	{
		const newValue = value.replace(/\u00A0/g, ' ');
		if (name === ControlName.city) 
		{
			this.service.buildEditDetailsForm.patchValue({
				city: this.sharedService.nonNumericSpaceRestriction(newValue)
			});
		}
		else if (name === ControlName.accountName) 
		{
			this.service.buildEditDetailsForm.patchValue({
				accountName: this.sharedUtilService.formatValue(newValue, name)
			});
		}
	}
	downloadLocationListData(id: string) 
	{
		this.service.downloadLocationList(id);
		const currentGridData = { ...this.state.sectionMiddle.tableState };
		currentGridData.gridData.forEach(row => 
		{
			if(row.id === id)
			{
				const cell = row.values.find(v => v.label === AccountLabels.buisnessAccount);
				if (cell && Array.isArray(cell.ctaList)) 
				{
					cell.ctaList.forEach(cta => 
					{
						if (cta.state && cta.state.disabled) 
						{
							cta.state.disabled = false;
							cta.state.icon.iconPath = Icons.approveIcon;
							if(cta.className === AprRejBtnType.singleReject)
							{
								cta.state.icon.iconPath = Icons.rejectIcon;
							}
							
						}
					});
				}
			}
			
		});
		this.state.sectionMiddle.tableState = currentGridData;
	}
	closeModalUpload()
	{
		this.service.resetUploadFileModelFlags();
		this.service.uploadModelState.buttons[0].disabled = false;
		this.service.uploadModelState.buttons[0].hidden = false;
		this.service.uploadModelState.buttons[1].hidden = false;
		this.service.uploadModelState.buttons[1].disabled = true;
		this.service.uploadModelState.buttons[0].label = this.service.modelButtons.cancel;
		this.service.uploadModelState.buttons[0].cssClass = CssClass.btnSecondary;
		this.service.uploadModelState.hidden = true;
		if(this.fileUploadComponent)
			this.fileUploadComponent.files = [];
	}
	uploadFileModalBtnEvent(event: ModalEvent)
	{
    	const btnIndex = event.state.buttons.findIndex((cta: ButtonState) => cta.label === event.cta.label);
		if (btnIndex === 1)
		{
			if(this.fileUploadComponent)
				this.uploadFileServerValidation(this.fileUploadComponent.files[0]);
		}
		else
		{
			this.uploadFile?.closePopup();
			this.closeModalUpload();
		}
	}
	uploadFileServerValidation(item: File)
	{
		this.service.checkServerValidationUploadFile(item);
	}
	replaceFileEventEmitter()
	{
		this.service.uploadModelState.buttons[0].disabled = false;
		this.service.uploadModelState.buttons[1].hidden = false;
		this.service.uploadModelState.buttons[1].disabled = true;
		if(this.fileUploadComponent)
			this.fileUploadComponent.files = [];
	}
	openUploadSection(row: GridValue) 
	{
		this.service.tempUserId = row.adminLocationID ?? '';
		this.sharedUtilService.hideModalBackdrop();
		this.service.uploadModelState.hidden = false;
		this.service.uploadFileState.isLoading = false;
	}
	fileValidationCheck(item: File)
	{
		const fileNameArr = item.name.split('.');
    	fileNameArr.pop();
    	const fileName = fileNameArr.join('.');
		if(fileNameArr.length !== 1 || fileName.trim().length === 0 || fileName.includes('/') || /\\/.exec(fileName) || item.name.split('.').pop()!=='xlsx') 
		{
			this.service.uploadFileState.errorHeading = ErrorMessageHeading.uploadFailed;
			this.service.uploadFileState.errorSubHeading = ErrorMessageSubHeading.wrongFormat;
			this.emptyUploadFilesArray();
		}
		else
		{
			this.checkOtherFileValidation(item);
		}
	}
	checkOtherFileValidation(item: File)
	{
		const fileSize = Math.round((item.size / 1024));
		if(fileSize > 102400) 
		{
			this.service.uploadFileState.errorHeading = ErrorMessageHeading.sizeExceed;
			this.service.uploadFileState.errorSubHeading = ErrorMessageSubHeading.sizeExceed;
			this.emptyUploadFilesArray();
		}
		else 
		{
			if(this.fileUploadComponent)
				this.fileUploadComponent.files[0] = item;
			this.service.uploadModelState.buttons[1].disabled = false;
			this.service.uploadModelState.buttons[1].hidden = false;
			this.service.uploadFileState.isSuccessClient = true;
			this.service.uploadFileState.isSuccessServer = false;
		}
	}
	emptyUploadFilesArray()
	{
		this.service.uploadModelState.buttons[1].hidden = true;
		this.service.uploadFileState.isError = true;
		this.service.uploadFileState.isSuccessClient = false;
		this.service.uploadFileState.isSuccessServer = false;
		if(this.fileUploadComponent)
			this.fileUploadComponent.files = [];
	}
}
